import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    IconButton,
    LinearProgress,
    Tab,
    Tabs,
    Tooltip,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getDataList } from '../../../helpers/getDataList';
import ReadMoreIcon from '@mui/icons-material/ReadMore';
import { DataGrid } from '@mui/x-data-grid';
import showMessage from '../../../helpers/showMessage';
import renderCellExpand from '../../feature/GridCellExpand';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from '../../../configs/messages';
import { deleteData } from '../../../helpers/deleteData';
import { useHistory } from 'react-router-dom';
import { ROUTES_LIST } from '../../../helpers/routesList';

function a11yProps(index) {
    return {
        id: `epg-tab-${index}`,
        'aria-controls': `epg-tabpanel-${index}`,
    };
}

// Өгөгдөл хувиргах helper функц
const transformProgramData = (dataArr) => {
    if (!Array.isArray(dataArr)) return [];
    return dataArr.map((data) => ({
        ...data,
        isRepeat: data.isRepeat === false ? 'Үгүй' : 'Тийм',
        isApproved: data.isApproved === '0' ? 'Үгүй' : 'Тийм',
        channel: data.channel ? data.channel.name : null,
        programType: data.programType ? data.programType.name : null,
        duration: data.duration ? formatDuration(data.duration) : data.duration,
    }));
};

// Duration форматлах
const formatDuration = (duration) => {
    const durationArr = duration.split(':');
    durationArr[1] =
        durationArr[1].length === 1 ? '0' + durationArr[1] : durationArr[1];
    durationArr[0] =
        durationArr[0].length === 1 ? '0' + durationArr[0] : durationArr[0];
    return durationArr.join(':');
};

export default function EpgDetail({ epgProgramId, isEdit }) {
    const [tabValue, setTabValue] = useState(0);
    const { enqueueSnackbar } = useSnackbar();
    const tab1Ref = useRef(null);
    const tab2Ref = useRef(null);

    // Мемоизацид байгаа columns - re-render хүүхлээс сэргийлэх
    const columns = useMemo(
        () => [
            {
                field: 'broadcastDate',
                headerName: 'Өдөр',
                width: '100',
            },
            {
                field: 'broadcastTime',
                headerName: 'Цаг',
                width: '100',
            },
            {
                field: 'duration',
                headerName: 'Үргэлжлэх хугацаа',
                width: '100',
            },
            {
                field: 'broadcastEndTime',
                headerName: 'Дуусах цаг',
                width: '100',
            },
            {
                field: 'name',
                headerName: 'Хөтөлбөрийн нэр',
                width: '180',
                renderCell: renderCellExpand,
            },
            {
                field: 'programType',
                headerName: 'Төрөл',
                flex: 1,
                width: '100',
                renderCell: renderCellExpand,
            },
            {
                field: 'imgUrl',
                headerName: 'Зураг',
                flex: 1,
                renderCell: (params) => {
                    return (
                        <div>
                            {params.value ? (
                                <img
                                    src={params.value}
                                    alt="зураг"
                                    style={{
                                        height: '30px',
                                        width: '40px',
                                        objectFit: 'cover',
                                    }}
                                />
                            ) : (
                                <></>
                            )}
                        </div>
                    );
                },
            },
            {
                field: 'edit',
                headerName: 'Засах',
                renderCell: (params) => {
                    return (
                        <div>
                            <Link
                                to={`/cms/dashboard/program/${epgProgramId}/${params.id}`}
                            >
                                <Tooltip title="Засах">
                                    <IconButton color="secondary">
                                        <ReadMoreIcon />
                                    </IconButton>
                                </Tooltip>
                            </Link>
                            <Tooltip title="Устгах">
                                <IconButton
                                    style={{ color: 'red' }}
                                    onClick={() => showDelete(params.id)}
                                >
                                    <DeleteOutlineIcon />
                                </IconButton>
                            </Tooltip>
                        </div>
                    );
                },
                width: '90',
            },
        ],
        [epgProgramId],
    );

    const [programList, setProgramList] = useState([]);
    const [selectedDateData, setSelectedDateData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [dateTabs, setDateTabs] = useState([]);
    const [showDeleteValidation, setShowDeleteValidation] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const history = useHistory();
    const [pageSize, setPageSize] = useState(100);
    const [inArchive, setInArchive] = useState(false);
    const localizedTextsMap = {
        columnMenuUnsort: 'Ангилаагүй',
        columnMenuSortAsc: 'Өсөхөөр эрэмблэх',
        columnMenuSortDesc: 'Буурахаар эрэмблэх',
        columnMenuFilter: 'Хайх',
        columnMenuHideColumn: 'Нуух',
        columnMenuShowColumns: 'Багана харах',
    };

    const updatedColumns = useMemo(
        () =>
            isEdit ? columns : columns.filter((col) => col.field !== 'edit'),
        [columns, isEdit],
    );

    useEffect(() => {
        let isActive = true;

        const logNetworkError = (error, context) => {
            const errorLog = {
                timestamp: new Date().toISOString(),
                context: context,
                epgProgramId: epgProgramId,
                status: error.response?.status,
                statusText: error.response?.statusText,
                errorMessage: error.response?.data?.message || error.message,
                errorData: error.response?.data,
                requestURL: error.config?.url,
                requestMethod: error.config?.method,
                userAgent: navigator.userAgent,
            };
            console.error('🔴 Network Error Log:', errorLog);
            return errorLog;
        };

        const fetchProgramDetails = async () => {
            try {
                // Loading эхлүүлэх
                setIsLoading(true);
                console.log(
                    '📡 Starting API request for program-detail-list/',
                    epgProgramId,
                );

                const startTime = performance.now();
                const response = await getDataList(
                    `program-detail-list/${epgProgramId}`,
                );
                const endTime = performance.now();

                console.log(
                    `⏱️  API request completed in ${(endTime - startTime).toFixed(2)}ms`,
                );

                if (isActive) {
                    console.log('✅ API response:', response);
                    const dataArr = response.data?.data || [];
                    const total = response.data?.total || 0;

                    if (dataArr.length === 0) {
                        console.warn('⚠️  No data found - marking as archive');
                        setInArchive(true);
                        setIsLoading(false);
                        return;
                    }

                    console.log(
                        `✅ Received ${dataArr.length} program records`,
                    );

                    const transformedData = transformProgramData(dataArr);

                    const tabs = transformedData.reduce((acc, curr) => {
                        const currentDate = curr.broadcastDate.slice(0, 10);
                        if (!acc.includes(currentDate)) {
                            acc.push(currentDate);
                        }
                        return acc;
                    }, []);

                    console.log(`✅ Generated ${tabs.length} date tabs`);

                    setProgramList(transformedData);
                    setDateTabs(tabs);

                    if (tabs.length && tab1Ref.current && tab2Ref.current) {
                        tab1Ref.current.click();
                        tab2Ref.current.click();
                    }

                    setIsLoading(false);
                }
            } catch (error) {
                if (isActive) {
                    logNetworkError(error, 'FETCH_PROGRAM_DETAIL_LIST');
                    const message = error.response?.data?.message
                        ? error.response.data.message
                        : error.message;
                    console.error(
                        '❌ Failed to fetch program details:',
                        message,
                    );
                    showMessage(message, enqueueSnackbar, 'error');
                    setIsLoading(false);
                }
            }
        };

        fetchProgramDetails();

        return () => {
            isActive = false;
        };
    }, [epgProgramId, enqueueSnackbar]);

    const handleChange = useCallback(
        (event, tabIndex) => {
            setTabValue(tabIndex);
            const selectedDateData = programList.filter((data) =>
                data.broadcastDate.includes(dateTabs[tabIndex]),
            );
            setSelectedDateData(selectedDateData);
        },
        [programList, dateTabs],
    );

    const showDelete = useCallback((id) => {
        setShowDeleteValidation(true);
        setDeleteId(id);
    }, []);

    const handleDeleteCancel = useCallback(() => {
        setShowDeleteValidation(false);
        setDeleteId(null);
    }, []);

    const handleDelete = useCallback(() => {
        setShowDeleteValidation(false);
        if (deleteId) {
            deleteData(`program-detail-list/inactive/${deleteId}`)
                .then(() => {
                    console.log(
                        '✅ Successfully deleted program detail:',
                        deleteId,
                    );
                    showMessage(SUCCESS_MESSAGES.DELETED_DATA, enqueueSnackbar);
                    history.push(ROUTES_LIST.epgPage);
                })
                .catch((error) => {
                    const errorLog = {
                        timestamp: new Date().toISOString(),
                        context: 'DELETE_PROGRAM_DETAIL',
                        deleteId: deleteId,
                        status: error.response?.status,
                        statusText: error.response?.statusText,
                        errorMessage:
                            error.response?.data?.message || error.message,
                        errorData: error.response?.data,
                        requestURL: error.config?.url,
                        requestMethod: error.config?.method,
                    };
                    console.error('🔴 Network Error Log:', errorLog);
                    showMessage(
                        error.response?.data?.message
                            ? error.response.data.message
                            : ERROR_MESSAGES.REQUEST_FAIL,
                        enqueueSnackbar,
                        'error',
                    );
                });
        }
    }, [deleteId, enqueueSnackbar, history]);

    const handleRefresh = useCallback(() => {
        if (dateTabs.length && dateTabs[tabValue]) {
            const selectedDate = dateTabs[tabValue];
            setIsLoading(true);

            getDataList(
                `program-detail-list/${epgProgramId}?date=${selectedDate}`,
            )
                .then((response) => {
                    console.log(
                        '✅ Refresh successful for date:',
                        selectedDate,
                        response,
                    );
                    const dataArr = response.data?.data || [];
                    const transformedData = transformProgramData(dataArr);
                    setSelectedDateData(transformedData);
                })
                .catch((error) => {
                    const errorLog = {
                        timestamp: new Date().toISOString(),
                        context: 'REFRESH_PROGRAM_DETAIL_LIST',
                        epgProgramId: epgProgramId,
                        selectedDate: dateTabs[tabValue],
                        status: error.response?.status,
                        statusText: error.response?.statusText,
                        errorMessage:
                            error.response?.data?.message || error.message,
                        errorData: error.response?.data,
                        requestURL: error.config?.url,
                        requestMethod: error.config?.method,
                    };
                    console.error('🔴 Network Error Log:', errorLog);
                    const message = error.response?.data?.message
                        ? error.response.data.message
                        : error.message;
                    showMessage(message, enqueueSnackbar, 'error');
                })
                .finally(() => setIsLoading(false));
        }
    }, [dateTabs, tabValue, epgProgramId, enqueueSnackbar]);

    return (
        <Box>
            {inArchive ? (
                <div
                    style={{
                        padding: '30px',
                        textAlign: 'center',
                        fontSize: 'x-large',
                    }}
                >
                    Энэ датаг архиваас харна уу.
                </div>
            ) : (
                <>
                    <Box sx={{ width: '100%' }} className="epg-detail-tab">
                        <Tabs
                            value={tabValue}
                            onChange={handleChange}
                            textColor="secondary"
                            indicatorColor="secondary"
                            aria-label="secondary tabs example"
                        >
                            {dateTabs.map((date, currIndex) =>
                                currIndex === 0 ? (
                                    <Tab
                                        id={`epgDetail${currIndex}`}
                                        key={date}
                                        label={date}
                                        ref={tab1Ref}
                                        {...a11yProps(date)}
                                    />
                                ) : currIndex === 1 ? (
                                    <Tab
                                        id={`epgDetail${currIndex}`}
                                        key={date}
                                        label={date}
                                        ref={tab2Ref}
                                        {...a11yProps(date)}
                                    />
                                ) : (
                                    <Tab
                                        id={`epgDetail${currIndex}`}
                                        key={date}
                                        label={date}
                                        {...a11yProps(date)}
                                    />
                                ),
                            )}
                        </Tabs>
                    </Box>

                    {isEdit && (
                        <Box
                            className="epg-detail-tab"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ width: '100%' }}
                            display="flex"
                        >
                            <Link
                                to={{
                                    pathname: `/cms/dashboard/program/${epgProgramId}/create`,
                                    selectedDate: dateTabs[tabValue],
                                }}
                            >
                                <Button
                                    size="small"
                                    color="secondary"
                                    variant="contained"
                                >
                                    {dateTabs[tabValue]}-ны хөтөлбөр нэмэх
                                </Button>
                            </Link>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={handleRefresh}
                                style={{
                                    margin: '5px 5px',
                                }}
                            >
                                Дахин ачааллах
                            </Button>
                        </Box>
                    )}

                    <Grid
                        container
                        justifyContent="center"
                        style={{ height: 630, width: '100%' }}
                    >
                        {isLoading ? (
                            <Box sx={{ width: '100%' }}>
                                <LinearProgress />
                            </Box>
                        ) : (
                            <>
                                <DataGrid
                                    rows={selectedDateData}
                                    columns={updatedColumns}
                                    pageSize={pageSize}
                                    onPageSizeChange={(newPage) =>
                                        setPageSize(newPage)
                                    }
                                    rowsPerPageOptions={[]}
                                    pagination
                                    localeText={localizedTextsMap}
                                    // checkboxSelection
                                    disableSelectionOnClick
                                />
                            </>
                        )}
                    </Grid>

                    <Dialog
                        open={showDeleteValidation}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">
                            Та энэхүү хөтөлбөрийг устгахыг хүсэж байна уу?
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                Та энэ хөтөлбөрийг устгахыг хүсэж байвал
                                &apos;Тийм&apos; товчийг дарна уу.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                variant="outlined"
                                onClick={handleDeleteCancel}
                            >
                                Болих
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={handleDelete}
                                autoFocus
                            >
                                Тийм
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            )}
        </Box>
    );
}
